import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { WalletProvider } from "@/context/WalletContext";
import { Toaster } from "@/components/ui/toaster";
import { WagmiProvider } from "wagmi";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { wagmiConfig } from "@/config/wagmi";
import "@/config/web3modal";
import { captureUTMs } from "@/utils/utm";

const queryClient = new QueryClient();

// Google Analytics 4 — fires a `page_view` event on every React Router navigation.
// gtag.js v4 normally auto-tracks history changes, but this hook is a belt-and-braces
// safety net so SPA route changes are never missed (e.g. when ad blockers partially
// load gtag, or when the auto-detection misses pushState patterns from React Router 6).
function useGoogleAnalyticsRouteTracker() {
  const location = useLocation();
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.gtag !== "function") return;
    const path = location.pathname + location.search;
    window.gtag("event", "page_view", {
      page_path: path,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [location.pathname, location.search]);
}

// Freeze any `utm_*` URL params into localStorage on route entry so BD forms
// can attribute the signup even if the visitor bounced through several
// pages before filling one out. See `src/utils/utm.js` for TTL + storage.
function useUTMPersistence() {
  const location = useLocation();
  useEffect(() => {
    captureUTMs();
  }, [location.search]);
}

function useScrollToTopOnRouteChange() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) window.scrollTo(0, 0);
  }, [pathname, hash]);
}

// Pages
import Landing from "@/pages/LandingV2";
import Auth from "@/pages/Auth";
import ConsumerDashboard from "@/pages/ConsumerDashboard";
import LenderDashboard from "@/pages/LenderDashboard";
import NewRequest from "@/pages/NewRequest";
import MyRequests from "@/pages/MyRequests";
import BrowseRFBs from "@/pages/BrowseRFBs";
import RFBDetail from "@/pages/RFBDetail";
import AIAnalysis from "@/pages/AIAnalysis";
import MyBids from "@/pages/MyBids";
import LenderPortfolio from "@/pages/LenderPortfolio";
import StakingPage from "@/pages/StakingPage";
import StakingHistory from "@/pages/StakingHistory";
import Resources from "@/pages/Resources";
import OnChainReceipts from "@/pages/OnChainReceipts";
import Governance from "@/pages/Governance";
import LogoReview from "@/pages/LogoReview";

// Static Pages
import About from "@/pages/About";
import Compliance from "@/pages/Compliance";
import MCP from "@/pages/MCP";
import KYPA from "@/pages/KYPA";
import KYPAInstrument from "@/pages/KYPAInstrument";
import KYPADirectory from "@/pages/KYPADirectory";
import KYPAAgentProfile from "@/pages/KYPAAgentProfile";
import BindingGate from "@/pages/BindingGate";
import BindingReceipt from "@/pages/BindingReceipt";
import SevenConditionsResponse from "@/pages/SevenConditionsResponse";
import FilmPriya from "@/pages/FilmPriya";
import FilmRecourse from "@/pages/FilmRecourse";
import Manifesto from "@/pages/Manifesto";
import HowDisputeWorks from "@/pages/HowDisputeWorks";
import VsExperianAgentTrust from "@/pages/VsExperianAgentTrust";
import AgentInsuranceQuestionnaire from "@/pages/AgentInsuranceQuestionnaire";
import ClaudeCommerceAgents from "@/pages/ClaudeCommerceAgents";
import ForMerchants from "@/pages/ForMerchants";
import ForB2BProcurement from "@/pages/ForB2BProcurement";
import ForIssuers from "@/pages/ForIssuers";
import ForUnderwriters from "@/pages/ForUnderwriters";
import KYA from "@/pages/KYA";
import ThreeAnalystsOneGap from "@/pages/ThreeAnalystsOneGap";
import ForPaymentsPlatforms from "@/pages/ForPaymentsPlatforms";
import Proofs from "@/pages/Proofs";
import Ops from "@/pages/Ops";
import Readiness from "@/pages/Readiness";
import RunbookUsdcHook from "@/pages/RunbookUsdcHook";
import HowItWorks from "@/pages/HowItWorks";
import Analytics from "@/pages/Analytics";
import FAQ from "@/pages/FAQ";
import Blog from "@/pages/Blog";
import Contact from "@/pages/Contact";
import Partners from "@/pages/Partners";
import Research from "@/pages/Research";
import Support from "@/pages/Support";
import Tokenomics from "@/pages/Tokenomics";
import OpenServDashboard from "@/pages/OpenServDashboard";
import Settings from "@/pages/Settings";
import AdminDashboard from "@/pages/AdminDashboard";
import AgentsPage from "@/pages/AgentsPage";
import LeaderboardPage from "@/pages/LeaderboardPage";
import LenderProtection from "@/pages/LenderProtection";
import BusinessDashboard from "@/pages/BusinessDashboard";
import ARManagement from "@/pages/ARManagement";
import BusinessLoanRequest from "@/pages/BusinessLoanRequest";
import BusinessProfile from "@/pages/BusinessProfile";
import PredictPage from "@/pages/PredictPage";
import MatchScorePage from "@/pages/MatchScorePage";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import Kutro5000 from "@/pages/Kutro5000";
import AirdropClaim from "@/pages/AirdropClaim";
import AgentCreditDashboard from "@/pages/AgentCreditDashboard";
import VelocityDashboard from "@/pages/VelocityDashboard";
import ArchitectAgreement from "@/pages/ArchitectAgreement";
import KutroQuest from "@/pages/KutroQuest";
import QuestPortal from "@/pages/QuestPortal";
import KutroHome from "@/pages/KutroHome";
import Litepaper from "@/pages/Litepaper";
import AIAgentsLive from "@/pages/AIAgentsLive";
import DocumentHistory from "@/pages/DocumentHistory";
import TermsOfService from "@/pages/TermsOfService";
import AaaSPage from "@/pages/AaaSPage";
import YieldSimulator from "@/pages/YieldSimulator";
import RegisterAgent from "@/pages/RegisterAgent";
import Developers from "@/pages/Developers";
import DeveloperPricing from "@/pages/DeveloperPricing";
import PaymentSuccess from "@/pages/PaymentSuccess";
import BDCheckpoints from "@/pages/BDCheckpoints";
import LangChain from "@/pages/LangChain";
import ForBanks from "@/pages/ForBanks";
import StatusPage from "@/pages/StatusPage";
import AdlMap from "@/pages/AdlMap";
import X402Audit from "@/pages/X402Audit";
import BindingGateLegal from "@/pages/BindingGateLegal";
import BindingGateSign from "@/pages/BindingGateSign";
import OpenConsentReceiptSpec from "@/pages/OpenConsentReceiptSpec";
import ConsentReceiptChecker from "@/pages/ConsentReceiptChecker";
import ClaimRecordChecker from "@/pages/ClaimRecordChecker";
import ClaimRecordDemo from "@/pages/ClaimRecordDemo";
import AgentReadyDemo from "@/pages/AgentReadyDemo";
import ExitRecordDraft from "@/pages/ExitRecordDraft";
import Security from "@/pages/Security";
import ClaimRecords from "@/pages/ClaimRecords";
import ClaimRecordComments from "@/pages/ClaimRecordComments";
import PilotSignup from "@/pages/PilotSignup";
import { RecourseDisclaimer } from "@/components/legal/RecourseDisclaimer";

// Protected Route Component
function ProtectedRoute({ children, allowedRoles }) {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    const redirectMap = { admin: '/admin', consumer: '/consumer', lender: '/lender', business: '/business' };
    return <Navigate to={redirectMap[user?.role] || '/consumer'} replace />;
  }

  return children;
}

// App Routes
function AppRoutes() {
  const { isAuthenticated, user } = useAuth();
  useGoogleAnalyticsRouteTracker();
  useUTMPersistence();
  useScrollToTopOnRouteChange();

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/logo-review" element={<LogoReview />} />
      <Route path="/about" element={<About />} />
      <Route path="/compliance" element={<Compliance />} />
      <Route path="/mcp" element={<MCP />} />
      <Route path="/kypa" element={<KYPA />} />
      <Route path="/kypa/instrument" element={<KYPAInstrument />} />
      <Route path="/kypa/directory" element={<KYPADirectory />} />
      <Route path="/kypa/agent/:kypa_id" element={<KYPAAgentProfile />} />
      <Route path="/binding-gate" element={<BindingGate />} />
      <Route path="/binding-gate/receipt/:receiptId" element={<BindingReceipt />} />
      <Route path="/binding-gate/sign" element={<BindingGateSign />} />
      <Route path="/specs/consent-receipt" element={<OpenConsentReceiptSpec />} />
      <Route path="/specs/consent-receipt/checker" element={<ConsentReceiptChecker />} />
      <Route path="/specs/claim-records" element={<ClaimRecords />} />
      <Route path="/specs/claim-records/checker" element={<ClaimRecordChecker />} />
      <Route path="/specs/claim-records/demo" element={<ClaimRecordDemo />} />
      <Route path="/specs/agent-ready/demo" element={<AgentReadyDemo />} />
      <Route path="/specs/agent-ready/exit-record" element={<ExitRecordDraft />} />
      <Route path="/specs/claim-records/comments" element={<ClaimRecordComments />} />
      <Route path="/specs/claim-records/pilot" element={<PilotSignup />} />
      <Route path="/binding-gate/terms" element={<BindingGateLegal kind="terms" />} />
      <Route path="/binding-gate/privacy" element={<BindingGateLegal kind="privacy" />} />
      <Route path="/seven-conditions" element={<SevenConditionsResponse />} />
      <Route path="/film/priya" element={<FilmPriya />} />
      <Route path="/film/recourse" element={<FilmRecourse />} />
      <Route path="/manifesto" element={<Manifesto />} />
      <Route path="/how-a-dispute-works" element={<HowDisputeWorks />} />
      <Route path="/vs/experian-agent-trust" element={<VsExperianAgentTrust />} />
      <Route path="/vs/agent-insurance-questionnaire" element={<AgentInsuranceQuestionnaire />} />
      <Route path="/vs/claude-commerce-agents" element={<ClaudeCommerceAgents />} />
      <Route path="/for-merchants" element={<ForMerchants />} />
      <Route path="/for-b2b-procurement" element={<ForB2BProcurement />} />
      <Route path="/for-issuers" element={<ForIssuers />} />
      <Route path="/for-underwriters" element={<ForUnderwriters />} />
      <Route path="/kya" element={<KYA />} />
      <Route path="/three-analysts-one-gap" element={<ThreeAnalystsOneGap />} />
      <Route path="/for-payments-platforms" element={<ForPaymentsPlatforms />} />
      <Route path="/for-banks" element={<ForBanks />} />
      <Route path="/proofs" element={<Proofs />} />
      <Route path="/ops" element={<Ops />} />
      <Route path="/readiness" element={<Readiness />} />
      <Route path="/admin/runbooks/usdc-hook" element={<RunbookUsdcHook />} />
      <Route path="/how-it-works" element={<HowItWorks />} />
      <Route path="/analytics" element={<Analytics />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/partners" element={<Partners />} />
      <Route path="/research" element={<Research />} />
      <Route path="/support" element={<Support />} />
      <Route path="/tokenomics" element={<Tokenomics />} />
      <Route path="/openserv" element={<OpenServDashboard />} />
      <Route path="/agents" element={<AIAgentsLive />} />
      <Route path="/leaderboard" element={<LeaderboardPage />} />
      <Route path="/predict" element={<PredictPage />} />
      <Route path="/match-score" element={<MatchScorePage />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/security" element={<Security />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/kutro5000" element={<Kutro5000 />} />
      <Route path="/claim" element={<AirdropClaim />} />
      <Route path="/quest" element={<QuestPortal />} />
      <Route path="/agent-credit" element={<AgentCreditDashboard />} />
      <Route path="/staking" element={<StakingPage />} />
      <Route path="/resources" element={<Resources />} />
      <Route path="/on-chain-receipts" element={<OnChainReceipts />} />
      <Route path="/kojiru-home" element={<KutroHome />} />
      <Route path="/litepaper" element={<Litepaper />} />
      <Route path="/aaas" element={<AaaSPage />} />
      <Route path="/yield-simulator" element={<YieldSimulator />} />
      <Route path="/register-agent" element={<RegisterAgent />} />
      <Route path="/developers" element={<Developers />} />
      <Route path="/developers/pricing" element={<DeveloperPricing />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/bd/checkpoints" element={<BDCheckpoints />} />
      <Route path="/langchain" element={<LangChain />} />
      <Route path="/for/banks" element={<ForBanks />} />
      <Route path="/status" element={<StatusPage />} />
      <Route path="/adl-m" element={<AdlMap />} />
      <Route path="/for/merchants" element={<AdlMap />} />
      <Route path="/x402-audit" element={<X402Audit />} />
      {/* Home qualify and Home Tracker are dormant: not offered. Redirect to Kojiru Home. */}
      <Route path="/home-qualify" element={<Navigate to="/kojiru-home" replace />} />
      <Route path="/consumer/home-tracker" element={<Navigate to="/kojiru-home" replace />} />
      <Route path="/lender/home-loans" element={<Navigate to="/kojiru-home" replace />} />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route 
        path="/auth" 
        element={
          isAuthenticated 
            ? <Navigate to={({ admin: '/admin', consumer: '/consumer', lender: '/lender', business: '/business' }[user?.role] || '/consumer')} replace /> 
            : <Auth />
        } 
      />

      {/* Consumer Routes */}
      <Route
        path="/consumer"
        element={
          <ProtectedRoute allowedRoles={['consumer']}>
            <ConsumerDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/consumer/new-request"
        element={
          <ProtectedRoute allowedRoles={['consumer']}>
            <NewRequest />
          </ProtectedRoute>
        }
      />
      <Route
        path="/consumer/requests"
        element={
          <ProtectedRoute allowedRoles={['consumer']}>
            <MyRequests />
          </ProtectedRoute>
        }
      />
      <Route
        path="/consumer/requests/:id"
        element={
          <ProtectedRoute allowedRoles={['consumer']}>
            <RFBDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/consumer/ai-analysis"
        element={
          <ProtectedRoute allowedRoles={['consumer']}>
            <AIAnalysis />
          </ProtectedRoute>
        }
      />
      <Route
        path="/consumer/documents"
        element={
          <ProtectedRoute allowedRoles={['consumer']}>
            <DocumentHistory />
          </ProtectedRoute>
        }
      />

      {/* Staking history (auth-gated to protect wallet/balance privacy) */}
      <Route
        path="/staking/history"
        element={
          <ProtectedRoute allowedRoles={['consumer', 'lender']}>
            <StakingHistory />
          </ProtectedRoute>
        }
      />
      <Route
        path="/governance"
        element={
          <ProtectedRoute allowedRoles={['consumer', 'lender', 'admin']}>
            <Governance />
          </ProtectedRoute>
        }
      />
      <Route
        path="/velocity"
        element={
          <ProtectedRoute allowedRoles={['consumer', 'lender', 'admin']}>
            <VelocityDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/architect-agreement"
        element={
          <ProtectedRoute allowedRoles={['consumer', 'lender', 'admin']}>
            <ArchitectAgreement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/genesis-quest"
        element={
          <ProtectedRoute allowedRoles={['consumer', 'lender', 'admin']}>
            <KutroQuest />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute allowedRoles={['consumer', 'lender', 'admin']}>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/consumer/wallet"
        element={
          <ProtectedRoute allowedRoles={['consumer']}>
            <StakingPage />
          </ProtectedRoute>
        }
      />

      {/* Lender Routes */}
      <Route
        path="/lender"
        element={
          <ProtectedRoute allowedRoles={['lender']}>
            <LenderDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/lender/rfbs"
        element={
          <ProtectedRoute allowedRoles={['lender']}>
            <BrowseRFBs />
          </ProtectedRoute>
        }
      />
      <Route
        path="/lender/rfbs/:id"
        element={
          <ProtectedRoute allowedRoles={['lender']}>
            <RFBDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/lender/bids"
        element={
          <ProtectedRoute allowedRoles={['lender']}>
            <MyBids />
          </ProtectedRoute>
        }
      />
      <Route
        path="/lender/portfolio"
        element={
          <ProtectedRoute allowedRoles={['lender']}>
            <LenderPortfolio />
          </ProtectedRoute>
        }
      />
      <Route
        path="/lender/protection"
        element={
          <ProtectedRoute allowedRoles={['lender']}>
            <LenderProtection />
          </ProtectedRoute>
        }
      />

      {/* Business Routes */}
      <Route
        path="/business"
        element={
          <ProtectedRoute allowedRoles={['business']}>
            <BusinessDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/business/receivables"
        element={
          <ProtectedRoute allowedRoles={['business']}>
            <ARManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/business/loan-request"
        element={
          <ProtectedRoute allowedRoles={['business']}>
            <BusinessLoanRequest />
          </ProtectedRoute>
        }
      />
      <Route
        path="/business/profile"
        element={
          <ProtectedRoute allowedRoles={['business']}>
            <BusinessProfile />
          </ProtectedRoute>
        }
      />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
    <WagmiProvider config={wagmiConfig}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <WalletProvider>
              <AppRoutes />
              <RecourseDisclaimer />
              <Toaster />
            </WalletProvider>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </WagmiProvider>
    </HelmetProvider>
  );
}

export default App;
